import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0B0F17] px-4 font-sans text-slate-100">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[130px]" />
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <Logo size={52} testId="login-logo" />
          <div>
            <h1 data-testid="login-heading" className="font-serif text-2xl tracking-tight">
              Agent Portal
            </h1>
            <p className="mt-1 text-sm text-slate-400">Hunt Shield Insurance Group · Authorized access only</p>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl"
        >
          <label htmlFor="login-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            Email
          </label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            className="ns-input"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <label htmlFor="login-password" className="mb-2 mt-5 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            Password
          </label>
          <input
            id="login-password"
            data-testid="login-password-input"
            type="password"
            className="ns-input"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          {error && (
            <p data-testid="login-error" className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E2A85C] px-7 py-3.5 font-semibold text-[#0B0F17] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/25 disabled:opacity-60"
          >
            {loading && <Loader2 size={17} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <a
          href="/"
          data-testid="login-back-link"
          className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400 transition-colors hover:text-[#E2A85C]"
        >
          <ArrowLeft size={15} /> Back to the main site
        </a>
      </div>
    </div>
  );
}
